import React, { useState } from 'react';
import { Breadcrumb } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { truncateLabel } from '../../utils/breadcrumbUtils';
import './BreadcrumbBar.css';

const MAX_DISPLAY_SEGMENTS = 5;
const TRUNCATION_THRESHOLD = 30;
const COLLAPSE_THRESHOLD = 6;

/**
 * Renders a segment label, wrapping in a span with title tooltip
 * when the label exceeds the truncation threshold.
 */
const SegmentLabel = ({ label }) => {
  if (!label) return null;

  const truncated = truncateLabel(label);
  const needsTooltip = label.length > TRUNCATION_THRESHOLD;

  if (needsTooltip) {
    return <span title={label}>{truncated}</span>;
  }

  return <>{truncated}</>;
};

/**
 * BreadcrumbBar - Shared breadcrumb navigation component.
 *
 * Renders the current breadcrumb trail from BreadcrumbContext.
 * Placed between the Navigation bar and page content in App.js.
 *
 * Features:
 * - Renders nothing when trail is empty
 * - Clickable segments navigate via useNavigate()
 * - Active (last) segment is non-interactive with aria-current="page"
 * - Responsive collapse: ellipsis for intermediate segments when 3+ segments
 * - Horizontal scroll on mobile (<768px)
 * - Labels truncated at 30 chars with tooltip showing full text
 */
const BreadcrumbBar = () => {
  const { breadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);

  if (breadcrumbs.length === 0) return null;

  const displaySegments = breadcrumbs.slice(0, MAX_DISPLAY_SEGMENTS);
  const shouldCollapse = collapsed && displaySegments.length >= COLLAPSE_THRESHOLD;

  const handleSegmentClick = (segment) => {
    if (segment.path) {
      try {
        navigate(segment.path, { state: segment.state });
      } catch (error) {
        console.error('Breadcrumb navigation error:', error);
      }
    }
  };

  const handleExpandClick = () => {
    setCollapsed(false);
  };

  const renderSegment = (segment, index, isLast) => {
    if (isLast) {
      return (
        <Breadcrumb.Item active aria-current="page" key={index}>
          <SegmentLabel label={segment.label} />
        </Breadcrumb.Item>
      );
    }

    if (segment.path) {
      return (
        <Breadcrumb.Item
          key={index}
          onClick={() => handleSegmentClick(segment)}
          linkProps={{ 'aria-label': `Navigate to ${segment.label}` }}
        >
          <SegmentLabel label={segment.label} />
        </Breadcrumb.Item>
      );
    }

    // Segment without a path (non-last) renders as non-interactive text
    // Note: We avoid using Breadcrumb.Item with `active` here because
    // react-bootstrap automatically adds aria-current="page" to active items,
    // which is semantically incorrect for non-last segments.
    return (
      <li key={index} className="breadcrumb-item breadcrumb-item-static">
        <SegmentLabel label={segment.label} />
      </li>
    );
  };

  const renderSegments = () => {
    if (shouldCollapse) {
      const firstSegment = displaySegments[0];
      const lastSegment = displaySegments[displaySegments.length - 1];

      return (
        <>
          {renderSegment(firstSegment, 0, false)}
          <Breadcrumb.Item key="ellipsis" active>
            <span
              className="breadcrumb-ellipsis"
              onClick={handleExpandClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleExpandClick();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Show hidden breadcrumb segments"
            >
              &hellip;
            </span>
          </Breadcrumb.Item>
          {renderSegment(lastSegment, displaySegments.length - 1, true)}
        </>
      );
    }

    return displaySegments.map((segment, index) => {
      const isLast = index === displaySegments.length - 1;
      return renderSegment(segment, index, isLast);
    });
  };

  return (
    <div className="breadcrumb-bar">
      <Breadcrumb label="Breadcrumb">
        {renderSegments()}
      </Breadcrumb>
    </div>
  );
};

export default BreadcrumbBar;
